/**
 * Theme system — manages light/dark mode with CSS custom properties.
 *
 * Brand color is set by the org config. All other colors derive from
 * the theme tokens. Persisted in localStorage per widget instance.
 */

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'mirlo-webchat-theme';

// Tokens consumed as `bg-w-*` / `text-w-*` etc in Tailwind. Tokens that
// participate in opacity utilities (e.g. `text-w-secondary/70`) MUST be
// expressed as space-separated RGB channels so Tailwind can synthesize
// `rgb(var(--w-*) / <alpha-value>)` correctly; using a hex here silently
// produces invalid CSS and the color falls back to the inherited value
// (black on dark backgrounds — which is exactly the "invisible text" bug).
// Non-opacity tokens can stay in whatever CSS-valid form is convenient.
export const tokens = {
  light: {
    '--w-hbg': 'var(--brand)',
    '--w-hfg': '#FFFFFF',
    '--w-hsub': 'rgba(255,255,255,.78)',
    '--w-bg': '#EDE8E0',
    '--w-bg-pattern':
      'radial-gradient(rgba(60,50,30,.06) 1px, transparent 1.4px)',
    '--w-bb-bg': '255 255 255',
    '--w-bb-fg': '#242D33',
    '--w-ub-bg': 'var(--brand)',
    '--w-ub-fg': '#FFFFFF',
    '--w-meta-bot': 'rgba(36,45,51,.42)',
    '--w-meta-user': 'rgba(255,255,255,.75)',
    '--w-tick-read': '#B9F1FF',
    '--w-composer-bg': '#F2F1EC',
    '--w-input-bg': '#FFFFFF',
    '--w-secondary': '122 135 145',
    '--w-border': '#DFDFE4',
  },
  dark: {
    '--w-hbg': '#151E24',
    '--w-hfg': '#E9EEF1',
    '--w-hsub': 'rgba(233,238,241,.65)',
    '--w-bg': '#0C1317',
    '--w-bg-pattern':
      'radial-gradient(rgba(255,255,255,.05) 1px, transparent 1.4px)',
    '--w-bb-bg': '30 42 50',
    '--w-bb-fg': '#E4EAEE',
    '--w-ub-bg': 'var(--brand-dark)',
    '--w-ub-fg': '#FFFFFF',
    '--w-meta-bot': 'rgba(228,234,238,.42)',
    '--w-meta-user': 'rgba(255,255,255,.75)',
    '--w-tick-read': '#6FD3FF',
    '--w-composer-bg': '#151E24',
    '--w-input-bg': '#233039',
    '--w-secondary': '138 151 160',
    '--w-border': '#2A3942',
  },
} as const;

export function getStoredTheme(): Theme | null {
  try {
    const val = localStorage.getItem(STORAGE_KEY);
    return val === 'light' || val === 'dark' ? val : null;
  } catch {
    return null;
  }
}

export function storeTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // localStorage unavailable (private browsing, etc.)
  }
}

/**
 * Darken a hex color by mixing it with black.
 * Used to derive the dark-mode user bubble from the brand color.
 */
export function darkenHex(hex: string, amount = 0.15): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  const dr = Math.round(r * (1 - amount));
  const dg = Math.round(g * (1 - amount));
  const db = Math.round(b * (1 - amount));

  return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
}
